// Finnish school holidays and national holidays as shaded-band periods.
// Dates are computed (Easter, midsummer, ISO weeks) so any school year works.

export type HolidayPeriod = {
    key: string;
    label: string;
    start: string; // YYYY-MM-DD (inclusive)
    end: string; // YYYY-MM-DD (inclusive)
    color: string;
};

// Amber = school break, blue-grey = national/religious holiday.
const BREAK_COLOR = 'rgba(255, 167, 38, 0.16)';
const HOLIDAY_COLOR = 'rgba(120, 144, 156, 0.18)';

const ymd = (d: Date): string => {
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${d.getUTCFullYear()}-${m}-${day}`;
};

const addDays = (d: Date, n: number): Date => {
    const r = new Date(d);
    r.setUTCDate(r.getUTCDate() + n);
    return r;
};

// Monday of a given ISO-8601 week number.
const isoWeekMonday = (year: number, week: number): Date => {
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon = 0
    const week1Monday = addDays(jan4, -jan4Dow);
    return addDays(week1Monday, (week - 1) * 7);
};

// Gregorian Easter Sunday (Anonymous / Meeus algorithm).
const easterSunday = (year: number): Date => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day));
};

// Midsummer Saturday (the Saturday between 20.–26.6.).
const midsummerSaturday = (year: number): Date => {
    for (let day = 20; day <= 26; day++) {
        const d = new Date(Date.UTC(year, 5, day));
        if (d.getUTCDay() === 6) return d;
    }
    return new Date(Date.UTC(year, 5, 20));
};

const holidaysForYear = (year: number): HolidayPeriod[] => {
    const week = (n: number) => {
        const mon = isoWeekMonday(year, n);
        return { start: ymd(mon), end: ymd(addDays(mon, 6)) };
    };
    const easter = easterSunday(year);
    const midsummer = midsummerSaturday(year);

    return [
        // National / religious single days.
        {
            key: `${year}-uusivuosi`,
            label: 'Uudenvuodenpäivä',
            start: `${year}-01-01`,
            end: `${year}-01-01`,
            color: HOLIDAY_COLOR,
        },
        {
            key: `${year}-loppiainen`,
            label: 'Loppiainen',
            start: `${year}-01-06`,
            end: `${year}-01-06`,
            color: HOLIDAY_COLOR,
        },
        {
            key: `${year}-vappu`,
            label: 'Vappu',
            start: `${year}-05-01`,
            end: `${year}-05-01`,
            color: HOLIDAY_COLOR,
        },
        {
            key: `${year}-itsenaisyys`,
            label: 'Itsenäisyyspäivä',
            start: `${year}-12-06`,
            end: `${year}-12-06`,
            color: HOLIDAY_COLOR,
        },
        {
            key: `${year}-juhannus`,
            label: 'Juhannus',
            start: ymd(addDays(midsummer, -1)),
            end: ymd(midsummer),
            color: HOLIDAY_COLOR,
        },
        {
            key: `${year}-joulu`,
            label: 'Joulu',
            start: `${year}-12-24`,
            end: `${year}-12-26`,
            color: HOLIDAY_COLOR,
        },
        // School breaks.
        {
            key: `${year}-syysloma`,
            label: 'Syysloma (vko 42)',
            ...week(42),
            color: BREAK_COLOR,
        },
        {
            key: `${year}-talviloma-8`,
            label: 'Talviloma (vko 8, etelä)',
            ...week(8),
            color: BREAK_COLOR,
        },
        {
            key: `${year}-talviloma-9`,
            label: 'Talviloma (vko 9, keski)',
            ...week(9),
            color: BREAK_COLOR,
        },
        {
            key: `${year}-talviloma-10`,
            label: 'Talviloma (vko 10, pohjoinen)',
            ...week(10),
            color: BREAK_COLOR,
        },
        {
            key: `${year}-paasiaisloma`,
            label: 'Pääsiäinen',
            start: ymd(addDays(easter, -2)), // pitkäperjantai
            end: ymd(addDays(easter, 1)), // 2. pääsiäispäivä
            color: BREAK_COLOR,
        },
    ];
};

// All holiday periods intersecting [fromYMD, toYMD] (inclusive, 'YYYY-MM-DD').
export const getFinnishHolidays = (
    fromYMD: string,
    toYMD: string
): HolidayPeriod[] => {
    const startYear = Number(fromYMD.slice(0, 4));
    const endYear = Number(toYMD.slice(0, 4));
    const periods: HolidayPeriod[] = [];
    for (let y = startYear; y <= endYear; y++) {
        periods.push(...holidaysForYear(y));
    }
    return periods.filter(p => p.end >= fromYMD && p.start <= toYMD);
};
